<template>
    <page :tabs="tabs" :busy="busy">
        <validation-observer class="d-block p-2" ref="editor_form" v-slot="{ meta, setFieldValue, errors }">
            <b-row class="flex-column flex-md-row no-gutters">
                <b-col md="4" class="pr-md-2 pr-0">
                    <validation-provider name="report_name" rules="required|maxMessageLen:40,Report Name">
                        <form-input
                            floatingLabel="Template Name"
                            v-model="template.report_name"
                            :error="errors.report_name"
                            @input="(val) => setFieldValue('report_name', val)"
                            :disabled="userIsUnlayerTester"
                        />
                    </validation-provider>
                </b-col>
                <b-col md="4" class="pr-md-2 pr-0">
                    <validation-provider name="report_type" rules="required">
                        <form-select
                            :options="reportTypeOptions"
                            floatingLabel="Type"
                            v-model="template.report_type"
                            :error="errors.report_type"
                            :disabled="template.is_default || userIsUnlayerTester"
                            @change="(val) => setFieldValue('report_type', val)"
                        />
                    </validation-provider>
                </b-col>
                <b-col md="4">
                    <validation-provider name="report_orientation" rules="required">
                        <form-select
                            :options="reportOrientationOptions"
                            floatingLabel="Orientation"
                            v-model="template.report_orientation"
                            :error="errors.report_orientation"
                            @change="(val) => setFieldValue('report_orientation', val)"
                            :disabled="userIsUnlayerTester"
                        />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row v-if="userIsUnlayerTester">
                <b-col md="6">
                    <b-alert show variant="info" class="mt-2">
                        Old templates are now locked following the deployment of our updated email/document editor
                        or because your account is currently transitioning. For all template creation and modification,
                        please utilize the new template editor system.
                    </b-alert>
                </b-col>
            </b-row>
            <validation-provider name="full_html" rules="required">
                <form-ck-editor
                    :editor-url="editorUrl"
                    ref="editor"
                    v-model="template.full_html"
                    :config="config"
                    @ready="insertHtmlComponent"
                    :error="errors.full_html"
                    @input="(val) => setFieldValue('full_html', val)"
                    :disabled="userIsUnlayerTester"
                ></form-ck-editor>
            </validation-provider>
            <div class="d-flex align-items-center">
                <b-button class="mr-2" :disabled="!meta.valid || userIsUnlayerTester" variant="primary" @click="save"
                    >Save</b-button
                >
                <b-button class="mr-2" :disabled="!meta.valid || userIsUnlayerTester" variant="default" @click="preview"
                    >Preview</b-button
                >
                <b-button variant="default" @click="goBack">Return</b-button>
            </div>

            <Teleport v-if="htmlMounted" to=".cke_toolbox">
                <span role="toolbar" aria-labelledby="cke_430_label" class="cke_toolbar d-block w-100" id="cke_430">
                    <div class="w-50 search-field" @click="focusInput">
                        <form-search-field
                            class="w-100 mb-0"
                            ref="search_field"
                            :options="htmlTagsOptions"
                            @input="(val) => onSearchTag(val)"
                            :busy="loading"
                            :value="tagSearch"
                            @select="selectTag"
                            :rightButton="rightButtonConfig"
                            :rightButton2="rightButtonConfig2"
                            placeholder="Search Tag"
                            @right-button-click="onRightButtonClick"
                            @right-button2-click="insertHtmlTag"
                            :isGrouped="true"
                        />
                    </div>
                </span>
            </Teleport>
        </validation-observer>
    </page>
</template>

<script>
import Page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import { formatResponseError } from '../../../utils/api';
import api from '../../../api';
import {
    DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_TYPE_ENDPOINT,
    DOCUMENT_REPORT_TEMPLATE_VARIABLES_ENDPOINT,
} from '../../../endpoints';
import FormCkEditor from '../../generic/form-ck-editor/form-ck-editor.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormSearchField from '../../generic/form-search-field/form-search-field.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import { mapGetters } from 'vuex';
export default {
    components: {
        Page,
        Grid,
        ResponsiveButton,
        FormCkEditor,
        FormInput,
        FormSelect,
        FormSearchField,
        FormTextarea,
    },
    data() {
        return {
            busy: false,
            loading: false,
            html: '',
            tags: [],
            template: {
                report_name: 'New Report Template',
                report_type: '',
                full_html: '',
                report_orientation: 165,
                is_default: false,
            },
            tagName: '',
            selectedTag: '',
            htmlMounted: false,
            tagSearch: '',
            htmlTagsOptions: [],
            reportTypeOptions: [],
        };
    },
    computed: {
        ...mapGetters('header', ['userIsUnlayerTester']),
        templateId() {
            return parseInt(this.$route.params.id);
        },
        editorUrl() {
            return 'https://cdn.ckeditor.com/4.19.0/full/ckeditor.js';
            // return '/static/ckeditor/ckeditor.js';
        },
        rightButtonConfig() {
            return {
                icon: ['fas', 'arrow-down'],
                variant: 'default',
            };
        },
        rightButtonConfig2() {
            return {
                variant: 'default',
                text: 'Insert Tag',
            };
        },
        config() {
            return {
                allowedContent: true,
                entities: false,
                enterMode: 1,
                shiftEnterMode: 1,
                versionCheck: false,
                disableNativeSpellChecker: true,
                height: 400,
                font_names:
                    "Lato/'Lato' , sans-serif;Arial/Arial, Helvetica, sans-serif;Arial Unicode/ArialUnicode;Comic Sans MS/Comic Sans MS, cursive;Courier New/Courier New, Courier, monospace;Georgia/Georgia, serif;Lucida Sans Unicode/Lucida Sans Unicode, Lucida Grande, sans-serif;Tahoma/Tahoma, Geneva, sans-serif;Times New Roman/Times New Roman, Times, serif;Trebuchet MS/Trebuchet MS, Helvetica, sans-serif;Verdana/Verdana, Geneva, sans-serif",
                contentsCss: [window.CKEDITOR?.getUrl('contents.css'), '/static/css/ckeditor4.css'],
                toolbar: [
                    {
                        name: 'clipboard',
                        items: ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo'],
                    },
                    {
                        name: 'editing',
                        items: ['Find', 'Replace', 'SelectAll'],
                    },
                    {
                        name: 'tools',
                        items: ['ShowBlocks'],
                    },
                    { name: 'links', items: ['Link', 'Unlink', 'Anchor'] },
                    {
                        name: 'insert',
                        items: ['Image', 'Table', 'HorizontalRule', 'SpecialChar'],
                    },
                    { name: 'document', items: ['Source'] },
                    '/',
                    {
                        name: 'basicstyles',
                        items: ['Bold', 'Italic', 'Underline', 'Strike', 'RemoveFormat'],
                    },
                    {
                        name: 'paragraph',
                        items: [
                            'NumberedList',
                            'BulletedList',
                            '-',
                            'Outdent',
                            'Indent',
                            '-',
                            'JustifyLeft',
                            'JustifyCenter',
                            'JustifyRight',
                            'JustifyBlock',
                        ],
                    },
                    {
                        name: 'styles',
                        items: ['Styles', 'Format', 'FontSize', 'Font', 'TextColor'],
                    },
                    { name: 'colors' },
                    '/',
                ],
            };
        },
        reportOrientationOptions() {
            return [
                {
                    value: 165,
                    text: 'Portrait',
                },
                {
                    value: 166,
                    text: 'Landscape',
                },
            ];
        },
        tabs() {
            return [
                {
                    title: `${this.templateId ? 'Edit' : 'Create'} Report Template`,
                    active: true,
                },
            ];
        },
    },
    mounted() {
        this.fetchAllData();
    },
    methods: {
        clearTags() {
            this.selectedTag = '';
            this.tagSearch = '';
        },
        async fetchAllData() {
            await this.fetchReportTemplate();
            await this.fetchReportTypes();
            await this.fetchTags();
            this.$refs.editor_form.setValues({
                report_name: this.template.report_name,
                report_type: this.template.report_type,
                report_orientation: this.template.report_orientation,
                full_html: this.template.full_html,
            });
        },
        async fetchReportTypes() {
            try {
                const { data } = await api.get(DOCUMENT_REPORT_TEMPLATE_TYPE_ENDPOINT());
                this.reportTypeOptions = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
                if (!this.templateId) {
                    this.template.report_type = this.reportTypeOptions[0].value;
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch report types' });
            } finally {
                this.busy = false;
            }
        },
        onSearchTag(value) {
            if (!value.trim()) {
                this.selectedTag = '';
            }
            const options = this.tags.map((item) => {
                return {
                    text: `${item.tag}: ${item.description}`,
                    value: item.tag,
                    group: item.group.toLowerCase(),
                };
            });
            const filteredOptions = options.filter((item) => item.text.toLowerCase().indexOf(value.toLowerCase()) > 0);
            if (
                options
                    .map((item) => {
                        return {
                            text: `${item.tag}: ${item.description}`,
                            value: item.tag,
                            group: item.group.toLowerCase(),
                        };
                    })
                    .some((item) => item.text.toLowerCase() !== value.toLowerCase())
            ) {
                this.selectedTag = '';
            }
            this.htmlTagsOptions = filteredOptions;
        },
        selectTag(text, value) {
            this.tagSearch = text;
            this.selectedTag = value;
        },
        async fetchReportTemplate() {
            if (this.templateId) {
                try {
                    this.busy = true;
                    const { data } = await api.get(`${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(this.templateId)}`);
                    if (this.templateId) {
                        this.template = data;
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch report template' });
                } finally {
                    this.busy = false;
                }
            }
        },
        async fetchTags() {
            try {
                const { data } = await api.get(DOCUMENT_REPORT_TEMPLATE_VARIABLES_ENDPOINT());
                this.htmlTagsOptions = data.map((item) => {
                    return {
                        text: `${item.tag}: ${item.description}`,
                        value: item.tag,
                        group: item.group.toLowerCase(),
                    };
                });
                this.tags = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch tags' });
            } finally {
                this.busy = false;
            }
        },
        focusInput() {
            // somehow tags input is not focusable, so we need to focus it on click
            const search = this.$refs.search_field;
            if (search.$children[0]) {
                if (search.$children[0].$children[0]) {
                    if (search.$children[0].$children[0].$children[0]) {
                        const inputDiv = search.$children[0].$children[0].$children[0].$el;
                        inputDiv.querySelector('input').focus();
                        inputDiv.querySelector('input').select();
                    }
                }
            }
        },
        insertHtmlTag() {
            if (window.CKEDITOR) {
                Object.values(CKEDITOR.instances).forEach((item) => {
                    if (this.selectedTag) {
                        item.insertHtml(this.selectedTag);
                    }
                });
            }
        },
        onRightButtonClick() {
            this.tagSearch = '  ';
            if (!this.tagSearch.trim()) {
                this.selectedTag = '';
            }
            const options = this.tags.map((item) => {
                return {
                    text: `${item.tag}: ${item.description}`,
                    value: item.tag,
                    group: item.group.toLowerCase(),
                };
            });
            this.htmlTagsOptions = options;
        },
        insertHtmlComponent() {
            this.htmlMounted = true;
        },
        async save() {
            try {
                this.busy = true;
                let warning = '';
                if (this.templateId) {
                    const { data } = await api.patch(DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(this.templateId), {
                        ...this.template,
                    });
                    warning = data.warning;
                } else {
                    const { data } = await api.post(DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(), {
                        ...this.template,
                    });
                    warning = data.warning;
                    this.$router.push(`/app/document/${data.id}/edit/`);
                }
                if (warning) {
                    this.warningToast({ text: data.warning });
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save report template' });
            } finally {
                this.busy = false;
            }
        },
        async preview() {
            await this.save();
            window.location.href = `${DOCUMENT_REPORT_TEMPLATE_BY_ID_ENDPOINT(this.templateId)}preview/`;
        },
        goBack() {
            this.$router.push(`/app/document/templates/`);
        },
    },
};
</script>

<style></style>

<style lang="scss" scoped>
:deep(.cke_toolbar fieldset) {
    border: none !important;
    padding: 0;
}

:deep(.cke_reset_all .input-group-append svg) {
    width: 10px;
}

:deep(.search-field) {
    .subscribers-list-wrapper {
        top: auto;
    }

    input {
        padding: 2px 5px !important;
        font-size: 12px !important;
    }

    .merge-guests-wrapper {
        width: auto !important;

        @media (max-width: 600px) {
            max-width: 80% !important;
        }
    }
}
</style>
